import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNumber, fmtDate, fmtError, STAGE_ORDER, BILLING_LABELS } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import SlaBadge from "@/components/SlaBadge";
import { Plus, SquaresFour, Table as TableIcon, Export, Receipt, X } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/csv";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Campaigns() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [view, setView] = useState("kanban");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [openQuickRaise, setOpenQuickRaise] = useState(false);

    const loadCampaigns = async () => {
        try {
            const { data } = await api.get("/campaigns");
            setData(data);
        } catch (e) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    const canCreate = user?.role === "client_servicing" || user?.role === "super_admin";
    const canSeeRevenue = ["super_admin", "accounts", "client_servicing"].includes(user?.role);
    const canQuickRaise = canCreate; // same RBAC as raising invoice requests

    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const selectedCampaigns = useMemo(
        () => data.filter((c) => selectedIds.has(c.id)),
        [data, selectedIds],
    );

    const exportCsv = () => {
        const stamp = new Date().toISOString().slice(0, 10);
        downloadCsv(
            `pulse-campaigns-${stamp}.csv`,
            [
                { key: "name", label: "Campaign" },
                { key: "client_org_name", label: "Client" },
                { key: "stage", label: "Stage" },
                { key: "platforms", label: "Platforms", value: (r) => (r.platforms || []).join(" | ") },
                { key: "pricing_model", label: "Pricing model" },
                { key: "volume", label: "Volume" },
                { key: "daily_run_rate", label: "Daily run rate" },
                { key: "revenue", label: "Revenue" },
                { key: "payout", label: "Payout" },
                { key: "billing_stage", label: "Billing stage" },
                { key: "start_date", label: "Start date" },
                { key: "end_date", label: "End date" },
            ],
            data,
        );
    };

    const grouped = useMemo(() => {
        const g = Object.fromEntries(STAGE_ORDER.map((s) => [s, []]));
        for (const c of data) {
            if (g[c.stage]) g[c.stage].push(c);
            else g.intake.push(c);
        }
        return g;
    }, [data]);

    const [draggingId, setDraggingId] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);

    const canDrag = ["super_admin", "execution_head", "client_servicing", "execution", "delivery_head", "delivery"].includes(user?.role);

    const onDragStart = (e, campaign) => {
        if (!canDrag) return;
        setDraggingId(campaign.id);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", campaign.id);
    };
    const onDragEnd = () => {
        setDraggingId(null);
        setDragOverStage(null);
    };
    const onDragOver = (e, stage) => {
        if (!canDrag) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragOverStage !== stage) setDragOverStage(stage);
    };
    const onDrop = async (e, newStage) => {
        if (!canDrag) return;
        e.preventDefault();
        const id = e.dataTransfer.getData("text/plain") || draggingId;
        setDragOverStage(null);
        setDraggingId(null);
        if (!id) return;
        const c = data.find((x) => x.id === id);
        if (!c || c.stage === newStage) return;
        // Optimistic update
        const prev = data;
        setData((arr) => arr.map((x) => (x.id === id ? { ...x, stage: newStage } : x)));
        try {
            await api.patch(`/campaigns/${id}/stage`, { stage: newStage });
            toast.success(`Moved to ${newStage}`);
        } catch (err) {
            setData(prev);
            toast.error(fmtError(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="overline mb-1">// Pipeline</div>
                    <h1 className="font-display text-4xl tracking-tighter">Campaigns</h1>
                    <p className="text-slate-500 mt-2 text-sm">{data.length} campaigns · drag through stages from intake to closed.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex border border-slate-200 bg-white">
                        <button
                            onClick={() => setView("kanban")}
                            data-testid="view-toggle-kanban"
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2 ${view === "kanban" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
                        >
                            <SquaresFour size={14} /> Kanban
                        </button>
                        <button
                            onClick={() => setView("table")}
                            data-testid="view-toggle-table"
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2 ${view === "table" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
                        >
                            <TableIcon size={14} /> Table
                        </button>
                    </div>
                    {canCreate && (
                        <Link
                            to="/campaigns/new"
                            data-testid="new-campaign-button"
                            className="bg-slate-900 text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-800"
                        >
                            <Plus size={14} weight="bold" /> New Campaign
                        </Link>
                    )}
                    <button
                        onClick={exportCsv}
                        disabled={data.length === 0}
                        data-testid="export-campaigns-csv"
                        title="Export current view to CSV"
                        className="bg-white border border-slate-300 text-slate-900 px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:border-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Export size={14} weight="bold" /> Export CSV
                    </button>
                </div>
            </header>

            {loading && <div className="text-xs font-mono text-slate-400">loading…</div>}

            {!loading && view === "kanban" && (
                <div className="kanban-scroller pb-4" data-testid="kanban-board">
                    <div className="flex gap-4 min-w-max">
                        {STAGE_ORDER.map((s) => (
                            <div
                                key={s}
                                className={`bg-slate-50 border w-[300px] flex flex-col transition-colors ${
                                    dragOverStage === s ? "border-slate-900 bg-blue-50" : "border-slate-200"
                                }`}
                                data-testid={`kanban-col-${s}`}
                                onDragOver={(e) => onDragOver(e, s)}
                                onDragLeave={() => dragOverStage === s && setDragOverStage(null)}
                                onDrop={(e) => onDrop(e, s)}
                            >
                                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
                                    <StatusBadge status={s} />
                                    <span className="font-mono text-xs text-slate-500">{grouped[s].length}</span>
                                </div>
                                <div className="flex flex-col gap-3 p-3 min-h-[300px]">
                                    {grouped[s].map((c) => (
                                        <div
                                            key={c.id}
                                            draggable={canDrag}
                                            onDragStart={(e) => onDragStart(e, c)}
                                            onDragEnd={onDragEnd}
                                            data-testid={`kanban-card-${c.id}`}
                                            className={`bg-white border border-slate-200 p-4 transition-all ${
                                                canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                                            } ${draggingId === c.id ? "opacity-40 scale-[0.98]" : "hover:border-slate-900"}`}
                                            onClick={(e) => {
                                                // Prevent navigation if user is mid-drag
                                                if (draggingId) return;
                                                navigate(`/campaigns/${c.id}`);
                                            }}
                                        >
                                            <div className="overline mb-1 truncate">{c.client_org_name || "—"}</div>
                                            <div className="font-medium text-sm mb-2 line-clamp-2">{c.name}</div>
                                            {c.sla?.applicable && (
                                                <div className="mb-2"><SlaBadge sla={c.sla} /></div>
                                            )}
                                            <div className="flex flex-wrap gap-1 mb-3">
                                                {(c.platforms || []).slice(0, 3).map((p) => (
                                                    <span key={p} className="font-mono text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5">{p}</span>
                                                ))}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
                                                {canSeeRevenue ? (
                                                    <div>
                                                        <div className="overline" style={{ fontSize: 9 }}>Revenue</div>
                                                        <div className="font-mono text-sm">{fmtMoney(c.revenue, c.currency)}</div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="overline" style={{ fontSize: 9 }}>Volume</div>
                                                        <div className="font-mono text-sm">{fmtNumber(c.volume)}</div>
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="overline" style={{ fontSize: 9 }}>End</div>
                                                    <div className="font-mono text-sm">{fmtDate(c.end_date)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {grouped[s].length === 0 && (
                                        <div className={`text-xs text-center py-8 font-mono ${dragOverStage === s ? "text-slate-900" : "text-slate-400"}`}>
                                            {dragOverStage === s ? "drop here ↓" : "— empty —"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!loading && view === "table" && (
                <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="campaigns-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                                {canQuickRaise && (
                                    <Th className="w-8">
                                        <input
                                            type="checkbox"
                                            checked={data.length > 0 && selectedIds.size === data.length}
                                            ref={(el) => {
                                                if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < data.length;
                                            }}
                                            onChange={(e) => {
                                                if (e.target.checked) setSelectedIds(new Set(data.map((c) => c.id)));
                                                else clearSelection();
                                            }}
                                            data-testid="campaigns-select-all"
                                            className="w-4 h-4 accent-slate-900"
                                            aria-label="Select all"
                                        />
                                    </Th>
                                )}
                                <Th>Campaign</Th>
                                <Th>Client</Th>
                                <Th>Stage</Th>
                                <Th>Platforms</Th>
                                {canSeeRevenue && <Th className="text-right">Revenue</Th>}
                                {canSeeRevenue && <Th>Billing</Th>}
                                <Th>Dates</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((c) => {
                                const isSel = selectedIds.has(c.id);
                                return (
                                    <tr
                                        key={c.id}
                                        onClick={() => navigate(`/campaigns/${c.id}`)}
                                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isSel ? "bg-blue-50" : ""}`}
                                        data-testid={`campaign-row-${c.id}`}
                                    >
                                        {canQuickRaise && (
                                            <Td>
                                                <input
                                                    type="checkbox"
                                                    checked={isSel}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => toggleSelected(c.id)}
                                                    data-testid={`campaign-row-check-${c.id}`}
                                                    className="w-4 h-4 accent-slate-900"
                                                    aria-label={`Select ${c.name}`}
                                                />
                                            </Td>
                                        )}
                                        <Td><span className="font-medium">{c.name}</span></Td>
                                        <Td className="text-slate-600">{c.client_org_name || "—"}</Td>
                                        <Td><StatusBadge status={c.stage} /></Td>
                                        <Td>
                                            <div className="flex flex-wrap gap-1">
                                                {(c.platforms || []).map((p) => (
                                                    <span key={p} className="font-mono text-[10px] uppercase bg-slate-100 px-1.5 py-0.5">{p}</span>
                                                ))}
                                            </div>
                                        </Td>
                                        {canSeeRevenue && <Td className="font-mono text-right">{fmtMoney(c.revenue, c.currency)}</Td>}
                                        {canSeeRevenue && <Td className="font-mono text-xs uppercase tracking-wider">{BILLING_LABELS[c.billing_stage] || c.billing_stage}</Td>}
                                        <Td className="font-mono text-xs text-slate-600">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {data.length === 0 && (
                        <div className="py-12 text-center text-sm text-slate-500">No campaigns yet.</div>
                    )}
                </div>
            )}

            {canQuickRaise && selectedIds.size > 0 && (
                <div
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white px-4 py-3 shadow-xl flex items-center gap-4"
                    data-testid="campaigns-selection-bar"
                >
                    <span className="font-mono text-xs uppercase tracking-wider">
                        {selectedIds.size} campaign{selectedIds.size === 1 ? "" : "s"} selected
                    </span>
                    <Button
                        onClick={() => setOpenQuickRaise(true)}
                        data-testid="campaigns-quick-raise"
                        className="rounded-none bg-white text-slate-900 hover:bg-slate-100 gap-2 h-8 text-xs"
                    >
                        <Receipt size={12} weight="bold" /> Raise invoice requests
                    </Button>
                    <button
                        type="button"
                        onClick={clearSelection}
                        data-testid="campaigns-clear-selection"
                        title="Clear selection"
                        className="text-slate-300 hover:text-white p-1"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {openQuickRaise && (
                <QuickRaiseModal
                    campaigns={selectedCampaigns}
                    onClose={() => setOpenQuickRaise(false)}
                    onCreated={() => {
                        setOpenQuickRaise(false);
                        clearSelection();
                        toast.success("Bulk request raised — view it on the Billing page");
                    }}
                />
            )}
        </div>
    );
}

function QuickRaiseModal({ campaigns, onClose, onCreated }) {
    const today = new Date();
    const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const [month, setMonth] = useState(defaultMonth);
    const [notes, setNotes] = useState("");
    const [items, setItems] = useState(() =>
        campaigns.map((c) => ({
            campaign_id: c.id,
            name: c.name,
            client_org_name: c.client_org_name,
            delivered_volume: c.volume || 0,
            amount: c.revenue || 0,
            currency: c.currency || "USD",
        })),
    );
    const [busy, setBusy] = useState(false);

    const totalsByCurrency = items.reduce((acc, it) => {
        const cur = it.currency || "USD";
        acc[cur] = (acc[cur] || 0) + (Number(it.amount) || 0);
        return acc;
    }, {});
    const currenciesUsed = Object.keys(totalsByCurrency).sort();

    const update = (id, patch) => {
        setItems((prev) => prev.map((it) => (it.campaign_id === id ? { ...it, ...patch } : it)));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!month.match(/^\d{4}-(0[1-9]|1[0-2])$/)) {
            toast.error("Enter month as YYYY-MM");
            return;
        }
        setBusy(true);
        try {
            const payloadItems = items.map((it) => ({
                campaign_id: it.campaign_id,
                delivered_volume: Number(it.delivered_volume) || 0,
                amount: Number(it.amount) || 0,
                currency: it.currency,
            }));
            const { data } = await api.post("/invoice-requests/bulk", {
                month,
                items: payloadItems,
                notes: notes || null,
            });
            const created = (data?.created || []).length;
            const errors = (data?.errors || []).length;
            if (errors > 0) toast.warning(`${created} raised · ${errors} failed`);
            else toast.success(`${created} invoice request${created === 1 ? "" : "s"} raised`);
            onCreated?.();
        } catch (err) {
            toast.error(fmtError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="quick-raise-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-3xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Quick raise</div>
                        <h3 className="font-display text-lg">Invoice requests · {campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="quick-raise-close">×</button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Month (YYYY-MM)</Label>
                            <Input
                                required
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                data-testid="quick-raise-month"
                                placeholder="2026-05"
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="overline mb-1.5 block">Shared note (optional)</Label>
                            <Input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Optional shared note for accounts…"
                                data-testid="quick-raise-notes"
                                className="rounded-none border-slate-300"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="overline mb-2 block">Items · edit volume / amount per row</Label>
                        <div className="border border-slate-200 max-h-[360px] overflow-y-auto scrollbar-thin">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2 overline" style={{ fontSize: 9 }}>Campaign</th>
                                        <th className="text-right px-3 py-2 overline" style={{ fontSize: 9 }}>Volume</th>
                                        <th className="text-right px-3 py-2 overline" style={{ fontSize: 9 }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it) => (
                                        <tr key={it.campaign_id} className="border-t border-slate-100" data-testid={`quick-raise-row-${it.campaign_id}`}>
                                            <td className="px-3 py-2 min-w-0">
                                                <div className="text-sm font-medium truncate">{it.name}</div>
                                                <div className="text-[10px] font-mono text-slate-500 truncate">
                                                    {it.client_org_name} · {it.currency}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input
                                                    type="number"
                                                    value={it.delivered_volume}
                                                    onChange={(e) => update(it.campaign_id, { delivered_volume: e.target.value })}
                                                    className="rounded-none border-slate-300 h-8 text-right font-mono w-28 ml-auto"
                                                    data-testid={`quick-raise-volume-${it.campaign_id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input
                                                    type="number"
                                                    value={it.amount}
                                                    onChange={(e) => update(it.campaign_id, { amount: e.target.value })}
                                                    className="rounded-none border-slate-300 h-8 text-right font-mono w-28 ml-auto"
                                                    data-testid={`quick-raise-amount-${it.campaign_id}`}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {items.length > 0 && (
                        <div className="bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between flex-wrap gap-3 text-sm">
                            <span><b>{items.length}</b> items · {month}</span>
                            <div className="flex flex-wrap gap-3">
                                {currenciesUsed.map((cur) => (
                                    <span key={cur} className="font-mono text-xs">
                                        {cur}: {fmtMoney(totalsByCurrency[cur], cur)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="submit"
                        disabled={busy || items.length === 0}
                        data-testid="quick-raise-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Sending…" : `Raise ${items.length} request${items.length === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </form>
        </div>
    );
}

const Th = ({ children, className = "" }) => (
    <th className={`text-left px-4 py-3 overline whitespace-nowrap ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
    <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>
);
